<script lang="ts" setup>
	import useDeferredLink from "~/composables/useDeferredLink";
	import type { IPost } from "~/server/models/post";

	definePageMeta({
		layout: "share",
	});

	const touch = useState("touch", () => false);

	const route = useRoute();

	const id = computed(() => route.params.id);

	const { data, pending, error } = await useFetch<IPost>(`/api/posts/${id.value}`, {
		watch: [id],
		/* query: {
			id: id.value,
		}, */
	});
	const viewLink = computed(() => useDeferredLink("post", id.value.toString()));
</script>

<template>
	<Head>
		<Meta property="og:image" :content="`/api/posts/${id}/og_image.jpg`" />
		<Meta property="og:image:width" content="1200" />
		<Meta property="og:image:height" content="630" />
		<Meta property="og:image:type" content="image/jpeg" />
		<Meta property="og:url" :content="`https://mimicmo.com/share/${id}`" />
	</Head>
	<main class="post flex-vertical">
		<header class="flex-vertical">
			<nuxt-link to="/" class="home">
				<Logo color="white" />
			</nuxt-link>
		</header>

		<div class="content flex-vertical">
			<template v-if="pending">
				<spinner size="large" color="white" />
			</template>

			<template v-else-if="error">
				<h2>Video could not be found</h2>
			</template>

			<template v-else>
				<spinner size="large" color="white" />

				<template v-if="data!.mux?.state === 'ready'">
					<MuxPoster :mux="data!.mux" />
					<MuxPlayer :mux="data!.mux" />
				</template>

				<template v-else-if="data!.cloudflare?.state === 'ready'">
					<CloudflarePoster :cloudflare="data!.cloudflare" />
					<CloudflarePlayer :cloudflare="data!.cloudflare" />
				</template>

				<template v-else-if="data!.r2?.filename">
					<R2Poster v-if="data!.r2.thumbnail ? true : false" :r2="data!.r2" />
					<R2Player :r2="data!.r2" />
				</template>

				<div class="download flex-vertical">
					<h1>Guess what I'm mimicking?</h1>
					<p class="large">Charades reimagined in short videos.</p>

					<a :href="viewLink" v-if="touch" class="flex-vertical button">
						<Button size="large" state="secondary">Open Mimicmo</Button>
						<p class="tiny">Available on the App Store</p>
					</a>

					<div class="flex-horizontal links" v-if="!touch">
						<AppStoreIcon :url="viewLink" />
						<QrCode />
					</div>
				</div>
			</template>
		</div>
	</main>
</template>

<style scoped lang="scss">
	main.post {
		width: 100%;
		height: 100%;
		position: relative;

		header {
			position: absolute;
			z-index: 10;
			top: 0;
			left: 0;
			width: 100%;
			height: 80px;
			padding-top: 30px;
			align-items: center;
			background: linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0) 100%);

			@include mobile {
				padding-top: 30px;
			}

			.home {
				.logo {
					width: auto;
					height: 28px;
					@include mobile {
						height: 24px;
					}
				}
			}
		}

		@include mobile {
			padding: 0;
		}

		.content {
			background: var(--black);
			width: 100%;
			height: 100%;
			align-items: center;
			justify-content: center;
			padding-top: 28px;
			padding-left: 28px;
			padding-right: 28px;
			padding-bottom: 28px;
			overflow: hidden;
			position: relative;

			@include mobile {
				padding-left: 12px;
				padding-right: 12px;
			}

			.mux-poster,
			.cloudflare-poster,
			.r2-poster {
				position: absolute;
				top: -1%;
				left: -1%;
				width: 102%;
				height: 102%;
				opacity: 0.2;
				filter: blur(20px);
			}

			.mux-player,
			.cloudflare-player,
			.r2-player {
				position: absolute;
				top: 0;
				left: 50%;
				transform: translateX(-50%);
				width: auto;
				height: 100%;
				@include mobile {
					transform: translateX(0);
					left: 0;
					width: 100%;
				}
			}

			.download {
				position: absolute;
				bottom: 0;
				left: 0;
				width: 100%;
				padding-top: 180px;
				padding-bottom: 30px;
				align-items: center;
				justify-content: center;
				flex-direction: column;
				text-align: center;
				background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%);
				z-index: 1;
				color: white;

				.links {
					gap: 12px;
					margin-top: 10px;
					z-index: 20;
				}

				@include mobile {
					padding-left: 28px;
					padding-right: 28px;
					padding-bottom: 18px;
				}

				a {
					width: 100%;
					max-width: 380px;
					@include mobile {
						max-width: 100%;
					}
					button {
						width: 100%;
					}
				}

				.button {
					gap: 5px;
				}

				h1 {
					margin-bottom: 8px;
					color: white;
				}

				p {
					margin-bottom: 24px;
					color: white;
				}

				a {
					width: 100%;
				}
			}
		}
	}
</style>
